import { useEffect, useState } from 'react';
import {
  Page, Card, Button, Banner, Text, TextField, Checkbox, Badge,
  DataTable, EmptyState, Spinner, Layout, BlockStack, InlineStack, Box, Link, Pagination,
} from '@shopify/polaris';
import { ChatIcon } from '@shopify/polaris-icons';
import { useSupportFaq } from '../../hooks/useSupportFaq';
import type { SupportFaqEntry } from '../../api/supportFaq';
import { IconBadge } from '../shared/IconBadge';

const HEX_PATTERN = /^#[0-9A-Fa-f]{6}$/;

function shadeColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const clamp = (v: number) => Math.max(0, Math.min(255, v));
  const r = clamp((num >> 16) + percent);
  const g = clamp(((num >> 8) & 0x00ff) + percent);
  const b = clamp((num & 0x0000ff) + percent);
  return `#${(0x1000000 + r * 0x10000 + g * 0x100 + b).toString(16).slice(1)}`;
}

function LivePreview({ settings }: { settings: { heading: string; accent_color: string } }) {
  const accent = HEX_PATTERN.test(settings.accent_color) ? settings.accent_color : '#111827';
  const gradient = `linear-gradient(135deg, ${accent}, ${shadeColor(accent, -30)})`;

  return (
    <div style={{ position: 'relative', minHeight: 260, background: '#f6f6f7', borderRadius: 8, padding: 20 }}>
      <div style={{ width: 324, maxWidth: '100%', background: '#fff', borderRadius: 16, boxShadow: '0 16px 48px rgba(0,0,0,0.28)', overflow: 'hidden' }}>
        <div style={{ padding: '16px 16px', color: '#fff', fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: gradient }}>
          <span>{settings.heading}</span>
          <span style={{ width: 26, height: 26, borderRadius: '50%', background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, lineHeight: 1 }}>×</span>
        </div>
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10, fontSize: 14, background: '#fbfbfd' }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
            <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#ececf3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0 }}>🤖</div>
            <div style={{ maxWidth: '78%', padding: '9px 13px', borderRadius: 14, borderBottomLeftRadius: 4, background: '#fff', border: '1px solid #eee', color: '#222' }}>
              Hi! Ask me about shipping, returns, or anything else about the store.
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ maxWidth: '78%', padding: '9px 13px', borderRadius: 14, borderBottomRightRadius: 4, background: '#ececec', color: '#111' }}>
              What&rsquo;s your return policy?
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: '1px solid #eee' }}>
          <input disabled placeholder="Ask a question…" style={{ flex: 1, padding: '10px 14px', border: '1.5px solid #e2e2ea', borderRadius: 999, fontSize: 13 }} />
          <button type="button" disabled style={{ padding: '9px 16px', border: 'none', borderRadius: 999, color: '#fff', fontSize: 13, fontWeight: 700, background: accent }}>Send</button>
        </div>
      </div>
      <div
        aria-hidden
        style={{
          position: 'absolute', bottom: 20, right: 20, width: 58, height: 58, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 25,
          boxShadow: '0 6px 20px rgba(0,0,0,0.25)', background: gradient,
        }}
      >
        💬
        <span style={{ position: 'absolute', top: -2, right: -2, width: 16, height: 16, borderRadius: '50%', background: '#ef4444', border: '2px solid #fff' }} />
      </div>
    </div>
  );
}

export function SupportFaqPanel() {
  const {
    entries, queries, topQuestions, settings, loading, saving, error, total, page, lastPage,
    fetchEntries, fetchQueries, fetchTopQuestions, fetchSettings,
    createEntry, updateEntry, deleteEntry, saveSettings, setError,
  } = useSupportFaq();

  const [form, setForm] = useState<{ enabled: boolean; heading: string; accent_color: string } | null>(null);
  const [saved, setSaved] = useState(false);
  const [newQuestion, setNewQuestion] = useState('');
  const [newAnswer, setNewAnswer] = useState('');

  useEffect(() => {
    fetchEntries(1);
    fetchQueries(1);
    fetchTopQuestions();
    fetchSettings();
  }, [fetchEntries, fetchQueries, fetchTopQuestions, fetchSettings]);

  useEffect(() => {
    if (settings) setForm(settings);
  }, [settings]);

  if (loading || !form) {
    return (
      <Page fullWidth title="Support FAQ Widget">
        <Layout>
          <Layout.Section>
            {loading ? (
              <InlineStack align="center"><Spinner /></InlineStack>
            ) : (
              <Banner tone="critical" action={{ content: 'Retry', onAction: fetchSettings }}>
                <p>{error ?? 'Failed to load support widget settings.'}</p>
              </Banner>
            )}
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const colorValid = HEX_PATTERN.test(form.accent_color);

  const handleSaveSettings = async () => {
    setSaved(false);
    const result = await saveSettings(form);
    if (result) setSaved(true);
  };

  const handleCreate = async () => {
    if (!newQuestion.trim() || !newAnswer.trim()) return;
    const result = await createEntry({ question: newQuestion.trim(), answer: newAnswer.trim() });
    if (result) {
      setNewQuestion('');
      setNewAnswer('');
    }
  };

  return (
    <Page fullWidth
      title="Support FAQ Widget"
      subtitle="A floating chat widget on your storefront that answers the same handful of pre-sale questions automatically — grounded only in facts you provide, never guessing"
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {error && (
              <Banner tone="critical" onDismiss={() => setError(null)}>
                <p>{error}</p>
              </Banner>
            )}
            {saved && (
              <Banner tone="success" onDismiss={() => setSaved(false)}>
                <p>Settings saved. Changes appear on your storefront within about a minute.</p>
              </Banner>
            )}

            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="center" wrap={false} gap="200">
                  <InlineStack gap="300" blockAlign="center" wrap={false}>
                    <IconBadge icon={ChatIcon} color="indigo" />
                    <Text as="h2" variant="headingMd">Widget Settings</Text>
                  </InlineStack>
                  <Badge tone={form.enabled ? 'success' : undefined}>{form.enabled ? 'Enabled' : 'Disabled'}</Badge>
                </InlineStack>

                <Checkbox
                  label="Show the support widget on my storefront"
                  checked={form.enabled}
                  onChange={(v) => setForm({ ...form, enabled: v })}
                />

                <TextField
                  label="Widget heading"
                  autoComplete="off"
                  value={form.heading}
                  onChange={(v) => setForm({ ...form, heading: v })}
                />

                <Box>
                  <Text as="p" variant="bodySm" tone="subdued">Accent color</Text>
                  <InlineStack gap="200" blockAlign="center">
                    <input
                      type="color"
                      value={colorValid ? form.accent_color : '#111827'}
                      onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                    />
                    <Box minWidth="100px">
                      <TextField label="" labelHidden autoComplete="off" value={form.accent_color} onChange={(v) => setForm({ ...form, accent_color: v })} />
                    </Box>
                  </InlineStack>
                  {!colorValid && (
                    <Text as="p" tone="critical" variant="bodySm">Color must be a 6-digit hex code, e.g. #111827.</Text>
                  )}
                </Box>

                <Box>
                  <Button variant="primary" loading={saving} disabled={!colorValid} onClick={handleSaveSettings}>
                    Save Settings
                  </Button>
                </Box>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">Live Preview</Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  This is exactly what shoppers see on your storefront — the chat bubble sits bottom-right on every page.
                </Text>
                <LivePreview settings={form} />
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Add an FAQ Answer</Text>
                <Text as="p" tone="subdued">
                  The widget only ever answers from what you write here (plus your Shopify policy pages) — it will
                  never invent a shipping time, return window, or price it doesn't know.
                </Text>
                <TextField
                  label="Question"
                  autoComplete="off"
                  placeholder="e.g. What are your shipping times?"
                  value={newQuestion}
                  onChange={setNewQuestion}
                />
                <TextField
                  label="Answer"
                  autoComplete="off"
                  multiline={3}
                  placeholder="e.g. We ship within 2-3 business days via USPS."
                  value={newAnswer}
                  onChange={setNewAnswer}
                />
                <Box>
                  <Button loading={saving} disabled={!newQuestion.trim() || !newAnswer.trim()} onClick={handleCreate}>
                    Add FAQ Entry
                  </Button>
                </Box>
              </BlockStack>
            </Card>

            {topQuestions.length > 0 && (
              <Card>
                <BlockStack gap="300">
                  <Text as="h2" variant="headingMd">Most Asked Questions</Text>
                  <Text as="p" tone="subdued">
                    What shoppers are actually asking — a repeated question here is a strong signal to turn into an
                    FAQ entry above.
                  </Text>
                  <DataTable
                    columnContentTypes={['text', 'numeric']}
                    headings={['Question', 'Times asked']}
                    rows={topQuestions.map((q) => [q.question, String(q.count)])}
                  />
                </BlockStack>
              </Card>
            )}

            {queries.length > 0 && (
              <Card>
                <BlockStack gap="300">
                  <Text as="h2" variant="headingMd">Recent Questions</Text>
                  <Text as="p" tone="subdued">
                    The full transcript of what the widget was asked and how it answered.
                  </Text>
                  <DataTable
                    columnContentTypes={['text', 'text', 'text']}
                    headings={['Question', 'Answer', 'Source']}
                    rows={queries.map((q) => [
                      q.question,
                      q.answer && q.answer.length > 80 ? `${q.answer.slice(0, 80)}…` : (q.answer ?? '—'),
                      <Badge tone={q.answered_from === 'ai' ? 'success' : 'attention'}>{q.answered_from}</Badge>,
                    ])}
                  />
                </BlockStack>
              </Card>
            )}

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">FAQ Entries ({total})</Text>

                {entries.length === 0 ? (
                  <EmptyState
                    heading="No FAQ entries yet"
                    image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  >
                    <p>Add your first FAQ answer above to start powering the storefront widget.</p>
                  </EmptyState>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text']}
                    headings={['Question', 'Answer', 'Status', 'Actions']}
                    rows={entries.map((entry: SupportFaqEntry) => [
                      entry.question,
                      entry.answer.length > 80 ? `${entry.answer.slice(0, 80)}…` : entry.answer,
                      <Badge tone={entry.is_active ? 'success' : undefined}>{entry.is_active ? 'Active' : 'Inactive'}</Badge>,
                      <InlineStack gap="200">
                        <Button
                          variant="plain"
                          size="slim"
                          onClick={() => updateEntry(entry.id, { is_active: !entry.is_active })}
                        >
                          {entry.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button variant="plain" tone="critical" size="slim" onClick={() => deleteEntry(entry.id)}>Delete</Button>
                      </InlineStack>,
                    ])}
                  />
                )}

                {lastPage > 1 && (
                  <InlineStack align="center">
                    <Pagination
                      hasPrevious={page > 1}
                      onPrevious={() => fetchEntries(page - 1)}
                      hasNext={page < lastPage}
                      onNext={() => fetchEntries(page + 1)}
                    />
                  </InlineStack>
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">One more step: enable it in your theme</Text>
                <Text as="p" tone="subdued">
                  Saving here configures the widget's content — you still need to turn it on for your live theme once:
                  go to <strong>Online Store &rarr; Themes &rarr; Customize &rarr; App embeds</strong>, then enable{' '}
                  <strong>Support FAQ Widget</strong>. This is a one-time toggle; all future FAQ edits take effect
                  automatically without touching the theme editor again.
                </Text>
                <Link url="https://help.shopify.com/en/manual/online-store/themes/theme-structure/extend/apps" external>
                  Learn more about app embeds in Shopify's docs
                </Link>
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
